import { setDateObj } from '@features/calendar/core';
import { getAllDateArr, setZeroPad } from '@utils/index';
import { IContentItem, IDataByDateMap, IDataItem, IDataMap, ISetDataItem } from '@type/index';

// 깊은 복사 - rtk state는 Immer에서 불가변(Immutable)하게 설정되어 있어 깊은 복사 진행함
export const deepCopy = (obj: object) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  return JSON.parse(JSON.stringify(obj));
};

// 컨텐츠 중 현재 월에 해당하는 데이터 추출 및 가공
export const setYearEmptyData = (year: number) => {
  if (!year) {
    return [];
  }

  const allDate = getAllDateArr(year);
  let yearEmptyData: string[][][] = [];

  allDate?.forEach((dateLng) => {
    const stringArr = new Array(dateLng).fill([]);
    yearEmptyData?.push(stringArr);
  });

  return yearEmptyData;
};

/*
        월 데이터에 contentItem object를 전부 복사해서 넣지 말고, 
        EVENT, EVENT-2 과 같이 {categoryId}-{dataListItemId} 로 id값을 만들어,
        해당 id값의 배열을 월 데이터에 넣어서 해시맵으로 만들기

        (ex) 2023.10월 데이터 = [[], [], ... [['EVENT-2', 'EVENT-59']... 31일], ...] // 12개월
            content데이터 가공 = 원본 데이터 -> {'EVENT-2': []}
*/
const setDataId = (depth1Id: string, depth2Id?: number) => {
  return `${depth1Id}${typeof depth2Id === 'number' ? `-${depth2Id}` : ''}`;
};

// dataList 데이터 가공
const setDataListItem = (contentData: IContentItem, dataItem: IDataItem): ISetDataItem | null => {
  if (!contentData || !dataItem) {
    return null;
  }

  return {
    ...dataItem,
    color: contentData?.color,
  };
};

const addDateByDataMap = ({
  dataByDateMap,
  depth2Id,
  startDate,
  endDate,
  days,
}: {
  dataByDateMap: IDataByDateMap;
  depth2Id: string;
  startDate: string;
  endDate: string;
  days: string[];
}) => {
  if (!dataByDateMap || !depth2Id || !startDate || !endDate) {
    return dataByDateMap;
  }

  const { year: startDateYear, month: startDateMonth, date: startDateDate } = setDateObj(startDate);
  const { year: endDateYear, month: endDateMonth, date: endDateDate } = setDateObj(endDate);
  const hasFilterDays = Array.isArray(days) && days?.length > 0;
  const filterDays = hasFilterDays ? days?.map((v) => (typeof v === 'string' ? parseInt(v) : null)) : [];

  for (let y = startDateYear; y <= endDateYear; y++) {
    if (!dataByDateMap[y]) {
      dataByDateMap[y] = setYearEmptyData(y);
    }

    const startMonthInThisYear = startDateYear === y ? startDateMonth : 1;
    const endMonthInThisYear = endDateYear === y ? endDateMonth : 12;

    for (let m = startMonthInThisYear; m <= endMonthInThisYear; m++) {
      let startDate = startDateYear === y && m === startDateMonth ? startDateDate : 1;
      let endDate = endDateYear === y && m === endDateMonth ? endDateDate : dataByDateMap[y][m - 1]?.length;
      let startDay = setDateObj(`${y}-${setZeroPad(m)}-${setZeroPad(startDate)}`)?.day;

      for (let d = startDate; d <= endDate; d++) {
        if (hasFilterDays) {
          const gap = (d - startDate) % 7;
          const day = (startDay + gap) % 7;
          if (!filterDays?.includes(day)) {
            continue;
          }
        }

        dataByDateMap[y][m - 1][d - 1] = [...dataByDateMap[y][m - 1][d - 1], depth2Id];
      }
    }
  }

  return dataByDateMap;
};

export const setDataByDate = (
  originData: IContentItem[],
  prevDataMap?: IDataMap,
  prevDataByDateMap?: IDataByDateMap,
): { dataMap: IDataMap; dataByDateMap: IDataByDateMap } => {
  let dataMap: IDataMap = { ...prevDataMap } || {};
  let dataByDateMap: IDataByDateMap = { ...prevDataByDateMap } || {};

  if (Array.isArray(originData) && originData?.length > 0) {
    originData?.forEach((depth1Data) => {
      const { id: categoryId, dataList } = depth1Data || {};

      if (!depth1Data || !categoryId) {
        return;
      }

      // depth1 정의
      const depth1Id = setDataId(categoryId);
      if (!dataMap[depth1Id]) {
        dataMap[depth1Id] = depth1Data;
      }

      // depth2 정의
      if (Array.isArray(dataList) && dataList?.length > 0) {
        dataList?.forEach((depth2Data) => {
          if (!depth2Data) {
            return;
          }

          const { id: itemId, startDate, endDate, days } = depth2Data || {};
          const depth2Id = setDataId(categoryId, itemId);

          if (!dataMap[depth2Id]) {
            dataMap[depth2Id] = setDataListItem(depth1Data, depth2Data);

            dataByDateMap = addDateByDataMap({
              dataByDateMap,
              depth2Id,
              startDate,
              endDate,
              days,
            });
          }
        });
      }
    });
  }

  //console.log('dataByDateMap', dataByDateMap, dataMap);

  return {
    dataByDateMap,
    dataMap,
  };
};

/*
    conti 이미지 -> webp 로 변환
    - url param으로 ?format=webp 붙이기
*/
export const setImgWebp = (url: string) => {
  if (!url) {
    return url;
  }

  const hasParam = /\?.+/g.test(url); // query param 있는지 여부
  const hasParams = /\?(?=.*&).+/g.test(url); // param 2개 이상 있는지 여부

  return `${url}${!hasParam ? '?' : ''}${hasParams ? '&' : ''}format=webp`;
};
